import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Ban, UserCheck } from "lucide-react";
import api from "@/lib/api";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/** Whether the signed-in member has blocked `targetUserId` (their OWN direction only —
 * the API never discloses an incoming block). */
export function useMyBlockState(targetUserId) {
  const [blocked, setBlocked] = useState(null);
  const load = React.useCallback(() => {
    if (!targetUserId) return;
    api.get(`/blocks/status/${targetUserId}`)
      .then(({ data }) => setBlocked(!!data.blocked))
      .catch(() => setBlocked(null));
  }, [targetUserId]);
  useEffect(() => { load(); }, [load]);
  return { blocked, reload: load, setBlocked };
}

/**
 * Block / Unblock entry point for an overflow menu. Renders the menu item plus its
 * confirmation dialog. Never says anything about who blocked whom.
 */
export function BlockMenuItem({ targetUserId, displayName, source, onChanged, testId }) {
  const { blocked, setBlocked } = useMyBlockState(targetUserId);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!targetUserId || blocked === null) return null;
  const name = displayName || "this member";

  const act = async () => {
    setBusy(true);
    try {
      if (blocked) {
        await api.delete(`/blocks/${targetUserId}`);
        setBlocked(false);
        toast.success(`${name} unblocked`);
      } else {
        await api.post("/blocks", { user_id: targetUserId, source });
        setBlocked(true);
        toast.success(`${name} blocked`);
      }
      setOpen(false);
      if (onChanged) onChanged();
    } catch (e) {
      toast.error((e && e.response && e.response.data && e.response.data.detail)
        || "Could not complete that right now");
    } finally { setBusy(false); }
  };

  return (
    <>
      <DropdownMenuItem
        className={blocked ? "" : "text-destructive focus:text-destructive"}
        onSelect={(e) => { e.preventDefault(); setOpen(true); }}
        data-testid={testId || (blocked ? "unblock-menu-item" : "block-menu-item")}
      >
        {blocked
          ? <><UserCheck className="mr-2 h-4 w-4" aria-hidden="true" /> Unblock member</>
          : <><Ban className="mr-2 h-4 w-4" aria-hidden="true" /> Block member</>}
      </DropdownMenuItem>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="block-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{blocked ? `Unblock ${name}?` : `Block ${name}?`}</AlertDialogTitle>
            <AlertDialogDescription>
              {blocked
                ? "You'll be able to interact again. Nothing that was removed when you blocked them comes back."
                : "Direct interaction between you stops. Any connection or follow between you is removed. Your existing history is kept, and activity you share inside a group may still be visible."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="block-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); act(); }}
              disabled={busy}
              className={blocked ? "" : "bg-destructive text-destructive-foreground"}
              data-testid="block-confirm"
            >
              {blocked ? "Unblock" : "Block"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
